import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {Empty, Nullable} from "data/types/generics";
import {Bindings} from "data/constants/bindings";
import type {ISquad, ISquadsStore} from "data/stores/squads/squds.store";
import {IMatch, IMarketsOdds} from "data/types/entities";
import {first, map} from "lodash";
import {MatchUtils} from "data/utils/match";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {buildConnextraLink, ConnextraPlace} from "data/utils/connextra";
import type {ICountryStore} from "data/stores/country/country.store";

export interface IMatchCardController extends ViewController {
	i18n: ILocalizationStore;
	get wdlHome(): string;
	get wdlAway(): string;
	get homeLink(): string;
	get homeClass(): Empty<string>;
	get awayClass(): Empty<string>;
	get gameTrackerLink(): string;

	getOdds(odds?: IMarketsOdds): IMarketsOdds | undefined;
	setMatch(match: IMatch): void;
	getSquadByID: (squadId: number) => Empty<ISquad>;
}

@injectable()
export class MatchCardController implements IMatchCardController {
	private _match: Nullable<IMatch> = null;

	constructor(
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.LocalizationStore) public i18n: ILocalizationStore,
		@inject(Bindings.CountryStore) public _countryStore: ICountryStore
	) {
		makeAutoObservable(this);
	}

	get wdlHome() {
		return this.getWDL(this._match?.homeSquad.id);
	}

	get wdlAway() {
		return this.getWDL(this._match?.awaySquad.id);
	}

	get homeClass() {
		if (!this._match || !this._match?.winner) {
			return;
		}

		return this._match.winner === this._match.homeSquad.id ? "win" : "loss";
	}

	get awayClass() {
		if (!this._match || !this._match?.winner) {
			return;
		}
		return this._match.winner === this._match.awaySquad.id ? "win" : "loss";
	}

	get homeLink() {
		if (this._match) {
			const squad = this.getSquadByID(this._match.homeSquad.id);
			const link =
				squad?.ticketUrls[this.i18n.locale] ?? first(map(squad?.ticketUrls)) ?? "#";

			return buildConnextraLink(link, ConnextraPlace.Tickets, this.i18n.locale);
		}

		return "#";
	}

	private get gameTrackerLinkState() {
		if (!this._match) {
			return "#preview";
		}

		if (MatchUtils.isPlaying(this._match)) {
			return "#gametracker";
		}

		return "#preview";
	}

	get gameTrackerLink() {
		if (!this._match || !this._match.cflId) {
			return "#";
		}

		const homeSquad = this.getSquadByID(this._match.homeSquad.id);
		const awaySquad = this.getSquadByID(this._match.awaySquad.id);

		if (!homeSquad || !awaySquad) {
			return "#";
		}

		const baseURL = {
			en: "https://www.cfl.ca/games/",
			fr: "https://www.lcf.ca/games/",
		}[this.i18n.locale];

		const homePath = homeSquad.name.toLowerCase().replace(" ", "-");
		const awayPath = awaySquad.name.toLowerCase().replace(" ", "-");
		const fixtureLink = `${this._match.cflId}/${homePath}-vs-${awayPath}/${this.gameTrackerLinkState}`;

		return buildConnextraLink(
			`${baseURL}${fixtureLink}`,
			ConnextraPlace.GameTracker,
			this.i18n.locale
		);
	}

	getOdds(odds?: IMarketsOdds): IMarketsOdds | undefined {
		if (!this._countryStore.isAllowed) {
			return;
		}

		return odds;
	}

	dispose(): void {
		return;
	}

	init(): void {
		return;
	}

	setMatch(match: IMatch) {
		this._match = match;
	}

	getSquadByID = (squadId: number): Empty<ISquad> => {
		return this._squadsStore.getSquadById(squadId);
	};

	private getWDL(squadID?: number) {
		if (squadID) {
			const squad = this.getSquadByID(squadID);

			if (squad) {
				return `${squad.wins}-${squad.loss}-${squad.draw}`;
			}
		}
		return "-";
	}
}
